import React, { useEffect } from "react";

import { css } from "@compiled/react";
import { UniformSlot } from "@uniformdev/canvas-react";

import { media } from "@atlaskit/primitives";

import { isClient } from "../../../utils";
import { spaceTokenValues } from "../../utils";

type UniformContainerProps = {
    marginTop?: keyof typeof spaceTokenValues;
    marginBottom?: keyof typeof spaceTokenValues;
    marginLeft?: keyof typeof spaceTokenValues;
    marginRight?: keyof typeof spaceTokenValues;
    paddingTop?: keyof typeof spaceTokenValues;
    paddingBottom?: keyof typeof spaceTokenValues;
    paddingLeft?: keyof typeof spaceTokenValues;
    paddingRight?: keyof typeof spaceTokenValues;
    horizontalAlignment?: UniformContainerHorizontalAlignment;
    verticalAlignment?: UniformContainerVerticalAlignment;
    orientation?: "horizontal" | "vertical";
    gap?: keyof typeof spaceTokenValues;
    hat?: SectionDividerDirection;
    pants?: SectionDividerDirection;
    backgroundColor?: string;
    sticky?: boolean;
    responsiveHiding?: ViewportSize[];
    overflow?: "visible" | "hidden";
};

type UniformContainerHorizontalAlignment = "default" | "center" | "start";
type UniformContainerVerticalAlignment = "default" | "center";

type SectionDividerDirection = "high-left" | "high-right";
type ViewportSize = "mobile" | "tablet" | "desktop";

// eslint-disable-next-line complexity
const UniformContainer: React.FC<UniformContainerProps> = ({
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    horizontalAlignment = "default",
    verticalAlignment = "default",
    orientation = "vertical",
    gap = "space.0",
    hat,
    pants,
    backgroundColor,
    sticky = false,
    responsiveHiding,
    overflow = "visible"
}) => {
    const marginStyle = css({
        marginBlockStart:
            "var(--ds-space-" + spaceTokenValues[marginTop ?? "space.0"] + ")",
        marginBlockEnd:
            "var(--ds-space-" +
            spaceTokenValues[marginBottom ?? "space.0"] +
            ")",
        marginInlineStart:
            "var(--ds-space-" + spaceTokenValues[marginLeft ?? "space.0"] + ")",
        marginInlineEnd:
            "var(--ds-space-" + spaceTokenValues[marginRight ?? "space.0"] + ")"
    });

    useEffect(() => {
        // Ensure that the header height is recalculated when the component mounts
        if (isClient) {
            const header = document.querySelector("header");
            if (header) {
                const headerHeight = header.offsetHeight;
                document.documentElement.style.setProperty(
                    "--dxp-header-height",
                    `${headerHeight}px`
                );
            }
        }
    }, []);

    const hiddenMobileStyle = css({
        [media.below.sm]: {
            display: "none"
        }
    });
    const hiddenTabletStyle = css({
        [media.only.sm]: {
            display: "none"
        }
    });
    const hiddenDesktopStyle = css({
        [media.above.md]: {
            display: "none"
        }
    });

    const colorStyle = css({
        "--dxp-block-background-color": backgroundColor
    });
    const innerStyle = css({
        background: "var(--dxp-block-background-color, transparent)",
        paddingBlockStart:
            "var(--ds-space-" + spaceTokenValues[paddingTop ?? "space.0"] + ")",
        paddingBlockEnd:
            "var(--ds-space-" +
            spaceTokenValues[paddingBottom ?? "space.0"] +
            ")",
        paddingInlineStart:
            "var(--ds-space-" +
            spaceTokenValues[paddingLeft ?? "space.0"] +
            ")",
        paddingInlineEnd:
            "var(--ds-space-" +
            spaceTokenValues[paddingRight ?? "space.0"] +
            ")",
        display: "flex",
        flexDirection: orientation === "horizontal" ? "row" : "column",
        gap: "var(--ds-space-" + spaceTokenValues[gap] + ")"
    });

    const horizontalAlignmentFlexValue = {
        center: "center",
        start: "flex-start",
        default: "initial"
    }[horizontalAlignment];

    const verticalAlignmentFlexValue = {
        center: "center",
        default: "flex-start"
    }[verticalAlignment];

    const alignmentStyleForHorizontalOrientation = css({
        alignItems: verticalAlignmentFlexValue,
        justifyContent: horizontalAlignmentFlexValue
    });

    const alignmentStyleForVerticalOrientation = css({
        justifyContent: verticalAlignmentFlexValue,
        alignItems: horizontalAlignmentFlexValue
    });

    const hiddenOnMobile = responsiveHiding?.includes("mobile");
    const hiddenOnTablet = responsiveHiding?.includes("tablet");
    const hiddenOnDesktop = responsiveHiding?.includes("desktop");

    const overflowStyle = css({
        overflow: overflow
    });

    return (
        <div
            css={[
                marginStyle,
                colorStyle,
                hat && hatCommonStyle,
                hat && hat === "high-right" ? hatHighRight : hatHighLeft,
                pants && pantsCommonStyle,
                pants && pants === "high-right"
                    ? pantsHighRight
                    : pantsHighLeft,
                hiddenOnMobile && hiddenMobileStyle,
                hiddenOnTablet && hiddenTabletStyle,
                hiddenOnDesktop && hiddenDesktopStyle,
                overflowStyle
            ]}
        >
            <div
                css={[
                    innerStyle,
                    orientation === "horizontal" &&
                        alignmentStyleForHorizontalOrientation,
                    orientation === "vertical" &&
                        alignmentStyleForVerticalOrientation,
                    sticky
                        ? css({
                              position: "sticky",
                              insetBlockStart: "var(--dxp-header-height, 0px)"
                          })
                        : ""
                ]}
            >
                <UniformSlot name="component" />
            </div>
        </div>
    );
};

const hatCommonStyle = css({
    "::before": {
        content: "",
        display: "block",
        height: "60px",
        background: "var(--dxp-block-background-color, transparent)",

        [media.above.sm]: {
            height: "80px"
        },
        [media.above.md]: {
            height: "120px"
        }
    }
});
const hatHighLeft = css({
    "::before": {
        clipPath: "polygon(0% 0%, 0% 100%, 100% 100%)"
    }
});
const hatHighRight = css({
    "::before": {
        clipPath: "polygon(100% 0%, 0% 100%, 100% 100%)"
    }
});

const pantsCommonStyle = css({
    "::after": {
        content: "",
        display: "block",
        height: "60px",
        background: "var(--dxp-block-background-color, transparent)",

        [media.above.sm]: {
            height: "80px"
        },
        [media.above.md]: {
            height: "120px"
        }
    }
});
const pantsHighLeft = css({
    "::after": {
        clipPath: "polygon(0% 100%, 100% 0%, 0% 0%)"
    }
});
const pantsHighRight = css({
    "::after": {
        clipPath: "polygon(100% 100%, 100% 0%, 0% 0%)"
    }
});

export default UniformContainer;
